import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Plus, Edit, Trash2, Save, X, Video, Headphones, FolderPlus } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import FileUploader from '@/components/ui/file-uploader';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface MeditationCategory {
  id?: string;
  name: string;
  description?: string;
  sort_order: number;
  is_active: boolean;
}

interface Meditation {
  id?: string;
  title: string;
  description: string;
  category: string;
  audio_url?: string;
  video_url?: string;
  thumbnail_url?: string;
  duration_minutes?: number;
  difficulty_level: string;
  meditation_type: string;
  tags: string[];
  is_published: boolean;
  play_count?: number;
}

export default function MeditationsManager() {
  const [meditations, setMeditations] = useState<Meditation[]>([]);
  const [categories, setCategories] = useState<MeditationCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingMeditation, setEditingMeditation] = useState<Meditation | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [editingCategory, setEditingCategory] = useState<MeditationCategory | null>(null);
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);

  const defaultMeditation: Meditation = {
    title: '',
    description: '',
    category: 'general',
    difficulty_level: 'beginner',
    meditation_type: 'guided',
    tags: [],
    is_published: false
  };

  useEffect(() => {
    loadMeditations();
    loadCategories();
  }, []);

  const loadMeditations = async () => {
    try {
      const { data, error } = await supabase
        .from('meditations')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setMeditations(data || []);
    } catch (error) {
      console.error('Error loading meditations:', error);
      toast.error('Failed to load meditations');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('meditation_categories')
        .select('*')
        .order('sort_order', { ascending: true });
      
      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error loading categories:', error);
      toast.error('Failed to load categories');
    }
  };

  const saveMeditation = async (meditation: Meditation) => {
    try {
      if (meditation.id) {
        const { error } = await supabase
          .from('meditations')
          .update(meditation)
          .eq('id', meditation.id);
        if (error) throw error;
        toast.success('Meditation updated successfully');
      } else {
        const { error } = await supabase
          .from('meditations')
          .insert([{ ...meditation, user_id: (await supabase.auth.getUser()).data.user?.id }]);
        if (error) throw error;
        toast.success('Meditation created successfully');
      }
      
      loadMeditations();
      setEditingMeditation(null);
      setIsCreating(false);
    } catch (error) {
      console.error('Error saving meditation:', error);
      toast.error('Failed to save meditation');
    }
  };

  const deleteMeditation = async (id: string) => {
    try {
      const { error } = await supabase
        .from('meditations')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      toast.success('Meditation deleted successfully');
      loadMeditations();
    } catch (error) {
      console.error('Error deleting meditation:', error);
      toast.error('Failed to delete meditation');
    }
  };

  const saveCategory = async (category: MeditationCategory) => {
    try {
      if (category.id) {
        const { error } = await supabase
          .from('meditation_categories')
          .update(category)
          .eq('id', category.id);
        if (error) throw error;
        toast.success('Category updated successfully');
      } else {
        const { error } = await supabase
          .from('meditation_categories')
          .insert([category]);
        if (error) throw error;
        toast.success('Category created successfully');
      }
      
      loadCategories();
      setEditingCategory(null);
      setIsCreatingCategory(false);
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error('Failed to save category');
    }
  };

  const deleteCategory = async (id: string) => {
    try {
      const { error } = await supabase
        .from('meditation_categories')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      toast.success('Category deleted successfully');
      loadCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Failed to delete category');
    }
  };

  const MeditationForm = ({ meditation, onSave, onCancel }: { 
    meditation: Meditation; 
    onSave: (meditation: Meditation) => void; 
    onCancel: () => void; 
  }) => {
    const [formData, setFormData] = useState(meditation);

    return (
      <Card className="mb-4">
        <CardHeader>
          <CardTitle>{meditation.id ? 'Edit Meditation' : 'Create New Meditation'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Meditation title"
            />
          </div>
          
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Meditation description"
              rows={4}
            />
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="category">Category</Label>
              <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.filter(c => c.is_active).map((cat) => (
                    <SelectItem key={cat.id} value={cat.name}>
                      {cat.name.charAt(0).toUpperCase() + cat.name.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="type">Type</Label>
              <Select value={formData.meditation_type} onValueChange={(value) => setFormData({ ...formData, meditation_type: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="guided">Guided</SelectItem>
                  <SelectItem value="hypnosis">Hypnosis</SelectItem>
                  <SelectItem value="visualization">Visualisation</SelectItem>
                  <SelectItem value="breathwork">Breathwork</SelectItem>
                  <SelectItem value="soundscape">Soundscape</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="difficulty">Difficulty Level</Label>
              <Select value={formData.difficulty_level} onValueChange={(value) => setFormData({ ...formData, difficulty_level: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="beginner">Beginner</SelectItem>
                  <SelectItem value="intermediate">Intermediate</SelectItem>
                  <SelectItem value="advanced">Advanced</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div>
            <FileUploader
              bucketName="meditation-audio"
              allowedTypes={['mp3', 'wav', 'm4a', 'aac']}
              maxSizeMB={100}
              currentUrl={formData.audio_url}
              onUploadComplete={(url) => setFormData({ ...formData, audio_url: url })}
              label="Meditation Audio (Required)"
              placeholder="Or paste audio URL"
            />
          </div>
          
          <div>
            <FileUploader
              bucketName="meditation-videos"
              allowedTypes={['mp4', 'mov', 'avi', 'mkv']}
              maxSizeMB={100}
              currentUrl={formData.video_url}
              onUploadComplete={(url) => setFormData({ ...formData, video_url: url })}
              label="Video Component (Optional)"
              placeholder="Or paste video URL (YouTube, Vimeo, etc.)"
            />
          </div>
          
          <div>
            <FileUploader
              bucketName="meditation-thumbnails"
              allowedTypes={['jpg', 'jpeg', 'png', 'webp']}
              maxSizeMB={10}
              currentUrl={formData.thumbnail_url}
              onUploadComplete={(url) => setFormData({ ...formData, thumbnail_url: url })}
              label="Thumbnail Image"
              placeholder="Or paste image URL"
            />
          </div>
          
          <div>
            <Label htmlFor="duration">Duration (minutes)</Label>
            <Input
              id="duration"
              type="number"
              value={formData.duration_minutes || ''}
              onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || undefined })}
              placeholder="20"
            />
          </div>
          
          <div>
            <Label htmlFor="tags">Tags (comma separated)</Label>
            <Input
              id="tags"
              value={formData.tags.join(', ')}
              onChange={(e) => setFormData({ 
                ...formData, 
                tags: e.target.value.split(',').map(t => t.trim()).filter(t => t) 
              })}
              placeholder="relaxation, mindfulness, stress relief"
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <Switch
              id="is_published"
              checked={formData.is_published}
              onCheckedChange={(checked) => setFormData({ ...formData, is_published: checked })}
            />
            <Label htmlFor="is_published">Published</Label>
          </div>
          
          <div className="flex gap-2">
            <Button onClick={() => onSave(formData)}>
              <Save className="h-4 w-4 mr-2" />
              Save
            </Button>
            <Button variant="outline" onClick={onCancel}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  const CategoryForm = ({ category, onSave, onCancel }: { 
    category: MeditationCategory; 
    onSave: (category: MeditationCategory) => void; 
    onCancel: () => void; 
  }) => {
    const [formData, setFormData] = useState(category);

    return (
      <Card className="mb-4">
        <CardHeader>
          <CardTitle>{category.id ? 'Edit Category' : 'Create New Category'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="cat-name">Category Name</Label>
            <Input
              id="cat-name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
              placeholder="e.g., stress_relief"
            />
          </div>
          
          <div>
            <Label htmlFor="cat-description">Description</Label>
            <Textarea
              id="cat-description"
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Category description"
              rows={3}
            />
          </div>
          
          <div>
            <Label htmlFor="cat-sort">Sort Order</Label>
            <Input
              id="cat-sort"
              type="number"
              value={formData.sort_order}
              onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <Switch
              id="cat-active"
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
            />
            <Label htmlFor="cat-active">Active</Label>
          </div>
          
          <div className="flex gap-2">
            <Button onClick={() => onSave(formData)}>
              <Save className="h-4 w-4 mr-2" />
              Save
            </Button>
            <Button variant="outline" onClick={onCancel}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return <div>Loading meditations...</div>;
  }

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold">Meditations Management</h2>
      
      <Tabs defaultValue="meditations">
        <TabsList>
          <TabsTrigger value="meditations">Meditations</TabsTrigger>
          <TabsTrigger value="categories">Categories</TabsTrigger>
        </TabsList>
        
        <TabsContent value="meditations" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Manage Meditations</h3>
            <Button onClick={() => setIsCreating(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add New Meditation
            </Button>
          </div>
          
          {isCreating && (
            <MeditationForm
              meditation={defaultMeditation}
              onSave={saveMeditation}
              onCancel={() => setIsCreating(false)}
            />
          )}
          
          {editingMeditation && (
            <MeditationForm
              meditation={editingMeditation}
              onSave={saveMeditation}
              onCancel={() => setEditingMeditation(null)}
            />
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {meditations.map((meditation) => (
              <Card key={meditation.id} className={!meditation.is_published ? 'opacity-60' : ''}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg">{meditation.title}</CardTitle>
                      <div className="flex gap-1 mt-1">
                        <Badge variant="secondary">{meditation.category}</Badge>
                        <Badge variant="outline">{meditation.meditation_type}</Badge>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditingMeditation(meditation)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => meditation.id && deleteMeditation(meditation.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-2">{meditation.description}</p>
                  <div className="flex items-center gap-2 mb-2">
                    {meditation.audio_url && <Headphones className="h-4 w-4 text-primary" />}
                    {meditation.video_url && <Video className="h-4 w-4 text-primary" />}
                    {meditation.duration_minutes && (
                      <span className="text-xs text-muted-foreground">{meditation.duration_minutes}m</span>
                    )}
                    {meditation.play_count !== undefined && (
                      <span className="text-xs text-muted-foreground">{meditation.play_count} plays</span>
                    )}
                  </div>
                  {meditation.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {meditation.tags.slice(0, 3).map((tag, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground">
                    {meditation.difficulty_level} | {meditation.is_published ? 'Published' : 'Draft'}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
        
        <TabsContent value="categories" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Meditation Categories</h3>
            <Button onClick={() => setIsCreatingCategory(true)}>
              <FolderPlus className="h-4 w-4 mr-2" />
              Add New Category
            </Button>
          </div>
          
          {isCreatingCategory && (
            <CategoryForm
              category={{ name: '', description: '', sort_order: categories.length + 1, is_active: true }}
              onSave={saveCategory}
              onCancel={() => setIsCreatingCategory(false)}
            />
          )}
          
          {editingCategory && (
            <CategoryForm
              category={editingCategory}
              onSave={saveCategory}
              onCancel={() => setEditingCategory(null)}
            />
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {categories.map((category) => (
              <Card key={category.id} className={!category.is_active ? 'opacity-60' : ''}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg capitalize">{category.name.replace(/_/g, ' ')}</CardTitle>
                      <Badge variant="secondary">Order: {category.sort_order}</Badge>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditingCategory(category)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => category.id && deleteCategory(category.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-2">{category.description}</p>
                  <div className="text-xs text-muted-foreground">
                    {category.is_active ? 'Active' : 'Inactive'}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}